import { Injectable } from '@angular/core';
import { Headers, Http, RequestOptions, Response } from '@angular/http';
import { Router } from '@angular/router';

import * as CryptoJS from 'crypto-browserify';
import { Observable } from 'rxjs/Observable';
import { catchError } from 'rxjs/operators';

import { Credentials, KeycloakCodes } from 'app/models';
import { OauthRepository } from 'app/repositories';
import { environment } from 'environments/environment';

@Injectable()
export class OAuthService {
  private static readonly HTTP_STATUS_CODE_UNAUTHORIZED = 401;
  private static readonly HTTP_STATUS_CODE_ACCESS_DENIED = 403;

  private readonly validTokenUrl = `${environment.apiUrl}/validToken`;
  private inactivityCheckTimer: number;

  constructor(
    private readonly http: Http,
    private readonly router: Router,
    private readonly repository: OauthRepository
  ) {}

  public initPkceFlow(): void {
    const codeVerifier = this.generateCodeVerifier();
    const challenge = this.base64URLEncode(this.generateS256CodeChallenge(codeVerifier));

    const csrfState = this.base64URLEncode(this.generateS256CodeChallenge(this.generateCodeVerifier()));

    this.repository.setKeycloakCodes(new KeycloakCodes(codeVerifier, challenge));
    this.repository.setKeycloakCsrfState(csrfState);

    this.redirectToKeycloakLogin();
  }

  public handleKeycloakCallback(code: string, sessionState: string, state: string): Observable<void> {
    const storedCsrfState = this.repository.getKeycloakCsrfState();
    if (!state || !storedCsrfState) {
      throw new Error('No valid CSRF states available');
    }

    if (storedCsrfState !== state) {
      throw new Error('CSRF state mismatched');
    }

    if (!this.repository.getKeycloakCodes()) {
      throw new Error('Inconsistent login state, no codes found in local storage');
    }

    return this.requestToken(code, sessionState);
  }

  public getAccessToken(): string {
    return this.repository.getCredentials().accessToken;
  }

  /**
   * Refreshes token. Errors, http 401 or 403 statuses will result in a logout and an observable ending with an
   * error result. Returns the new access token.
   *
   * @private
   */
  public performTokenRefresh(): Observable<string> {
    const body =
      `grant_type=refresh_token&` +
      `client_id=${environment.authConfigClientId}&` +
      `refresh_token=${this.repository.getCredentials().refreshToken}`;

    return this.http
      .post(environment.authTokenIssuer, body, this.optionsWithHeaderXFormUrlEncoded())
      .pipe(
        catchError(err => {
          this.logout();
          throw new Error(`Error occurred while perform token refresh, error message: ${err.message}`);
        })
      )
      .map((response: Response) => {
        if (
          response.status === OAuthService.HTTP_STATUS_CODE_UNAUTHORIZED ||
          response.status === OAuthService.HTTP_STATUS_CODE_ACCESS_DENIED
        ) {
          this.logout();
          throw new Error(`Cannot refresh token, refresh responded with http status: ${response.status}`);
        }

        return this.storeAccessAndRefreshTokensAndReturnAccessToken(response);
      });
  }

  public isLoggedIn(): boolean {
    return this.repository.getCredentials() !== null;
  }

  public getRoles(): string[] {
    return this.repository.getCredentials().roles;
  }

  public logout() {
    if (!this.isLoggedIn()) {
      return;
    }

    this.stopInactivityCheckTimer();
    this.performKeycloakLogout();
  }

  /**
   * Once you've created the code_verifier and the code_challenge, you must get the user's authorization.
   * This is technically the beginning of the authorization flow,
   */
  private redirectToKeycloakLogin() {
    const csrfState = this.repository.getKeycloakCsrfState();
    const keycloakCodes = this.repository.getKeycloakCodes();

    const url =
      `${environment.authConfigIssuer}?` +
      `response_type=${environment.authConfigResponseType}&` +
      `code_challenge=${keycloakCodes.challenge}&` +
      `code_challenge_method=${environment.authConfigCodeChallengeMethod}&` +
      `client_id=${environment.authConfigClientId}&` +
      `redirect_uri=${window.location.origin + environment.authConfigRedirectUri}&` +
      `scope=${environment.authConfigScope}&` +
      `state=${csrfState}`;

    window.location.replace(url);
  }

  private requestToken(code: string, sessionState: string): Observable<void> {
    const keycloakCodes = this.repository.getKeycloakCodes();

    const body =
      `grant_type=authorization_code&` +
      `client_id=${environment.authConfigClientId}&` +
      `redirect_uri=${window.location.origin + environment.authConfigRedirectUri}&` +
      `session_state=${sessionState}&` +
      `code_verifier=${keycloakCodes.codeVerifier}&` +
      `code=${code}`;

    return this.http
      .post(environment.authTokenIssuer, body, this.optionsWithHeaderXFormUrlEncoded())
      .map((result: Response) => {
        this.storeAccessAndRefreshTokensAndReturnAccessToken(result);
        this.startInactivityCheckTimer();
      });
  }

  private headersWithoutCredentials(): Headers {
    return new Headers({ 'Content-Type': 'application/json' });
  }

  private optionsWithHeaderXFormUrlEncoded(): RequestOptions {
    return new RequestOptions({ headers: this.headersXFormUrlEncoded() });
  }

  private headersXFormUrlEncoded(): Headers {
    const headers = new Headers({ 'Content-Type': 'application/x-www-form-urlencoded' });
    headers.set('accept', 'application/json');
    return headers;
  }

  private storeAccessAndRefreshTokensAndReturnAccessToken(response: Response): string {
    const tokens = response.json();
    const decodedToken = this.getDecodedAccessToken(tokens.access_token);

    this.repository.setCredentials(
      new Credentials(
        decodedToken.prefered_username,
        tokens.access_token,
        tokens.refresh_token,
        tokens.id_token,
        this.getRolesFromToken(decodedToken)
      )
    );
    return tokens.access_token;
  }

  private getRolesFromToken(token) {
    if (
      token == null ||
      token.resource_access === null ||
      token.resource_access[environment.authConfigClientId] == null ||
      token.resource_access[environment.authConfigClientId].roles == null
    ) {
      console.log(`No resource_access.${environment.authConfigClientId}.roles in access token payload`);
      return [];
    }

    return token.resource_access[environment.authConfigClientId].roles;
  }

  private startInactivityCheckTimer(): void {
    const toMilliSeconds = 1000;

    if (typeof this.inactivityCheckTimer === 'undefined') {
      this.inactivityCheckTimer = window.setInterval(
        () => this.checkForInactivity(),
        toMilliSeconds * environment.authInactivityIntervalInSeconds
      );
    }
  }

  private stopInactivityCheckTimer(): void {
    if (typeof this.inactivityCheckTimer !== 'undefined') {
      clearInterval(this.inactivityCheckTimer);
      this.inactivityCheckTimer = undefined;
    }
  }

  /**
   * Re-using current inactivity logic until old login method can be removed and a proper httpInterceptor
   * can then be implemented.
   */
  private checkForInactivity(): void {
    const credentials = this.repository.getCredentials();

    if (!credentials) {
      this.stopInactivityCheckTimer();
      return;
    }

    const headers = this.headersWithoutCredentials();
    headers.set('Authorization', `Bearer ${credentials.accessToken}`);

    this.http.get(this.validTokenUrl, new RequestOptions({ headers })).subscribe(
      (response: Response) => {
        // Nothing to do, we are still logged in
      },
      (response: Response) => {
        if (response.status === OAuthService.HTTP_STATUS_CODE_UNAUTHORIZED) {
          this.performTokenRefresh().subscribe();
        } else {
          this.logout();
        }
      }
    );
  }

  private performKeycloakLogout() {
    const { idToken } = this.repository.getCredentials();
    const url = `${environment.authTokenLogout}?id_token_hint=${idToken}&post_logout_redirect_uri=${encodeURIComponent(
      window.location.origin
    )}`;
    this.repository.deleteAuthData();

    window.location.replace(url);
  }

  /**
   * This function is used to decode a jwt token using the built in atob() method.
   */
  private getDecodedAccessToken(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  private base64URLEncode(str) {
    return str.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Generate a code_challenge from the code_verifier that will be sent to Auth0 to request an authorization_code.
   */
  private generateS256CodeChallenge(buffer: string) {
    return CryptoJS.createHash('sha256').update(buffer).digest();
  }

  /**
   * Generate a code_verifier that will be sent to Auth0 to request tokens.
   * Create a code_verifier, which is a cryptographically random key that will eventually be sent to Auth0 to request tokens
   */
  private generateCodeVerifier() {
    const bytes = CryptoJS.randomBytes(32);
    return this.base64URLEncode(bytes);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/oauth.service.ts